/**
 * React Query hooks for Rewards admin API.
 *
 * Calls zaviago_backend.rewards.admin_api endpoints.
 * Company is derived from the user's session on the backend —
 * no tenant param needed for admin APIs.
 *
 * Plan 177, Epic 02 — Admin Setup Wizard
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { useAuthStore } from "@/lib/store/authStore";

/**
 * Active store key for scoping rewards admin calls to one storefront's program
 * (Plan 1933). Reads scope to the selected store (merged when null on multi-store);
 * writes require it (backend throws "select a store" otherwise on multi-store).
 */
export function useRewardsStoreKey(): string | undefined {
  return useAuthStore((s) => s.activeStorefrontKey) ?? undefined;
}

// ---------------------------------------------------------------------------
// Types — match Python return shapes exactly (frappeMethod unwraps .message)
// ---------------------------------------------------------------------------

export interface RewardsConfigResponse {
  configured: boolean;
  name?: string;
  loyalty_program_name?: string;
  collection_factor?: number;
  conversion_factor?: number;
  expiry_duration?: number;
  auto_opt_in?: number;
  from_date?: string | null;
  to_date?: string | null;
  customer_group?: string | null;
  customer_territory?: string | null;
  tiers?: Array<{
    tier_name: string;
    min_spent: number;
    collection_factor: number;
    tier_icon?: string | null;
    tier_card_image?: string | null;
    tier_card_design?: TierCardDesign | null;
  }>;
}

export interface EarnRuleItem {
  name: string;
  earn_type: string;
  points: number;
  enabled: number;
  max_per_customer: number;
  description: string | null;
}

export interface EarnRulesResponse {
  rules: EarnRuleItem[];
}

export interface RewardCatalogItem {
  name: string;
  title: string;
  description: string | null;
  reward_type: string;
  points_cost: number;
  image: string | null;
  status: string;
  max_redemptions: number;
  current_redemptions: number;
  display_order: number;
  valid_from: string | null;
  valid_upto: string | null;
}

export interface AdminRewardsResponse {
  rewards: RewardCatalogItem[];
}

export interface RewardsMetricsResponse {
  total_members: number;
  points_issued: number;
  points_redeemed: number;
  redemption_rate: number;
}

export interface MemberItem {
  customer: string;
  customer_name: string;
  tier: string;
  points_balance: number;
  orders_last_12m: number;
  spent_last_12m: number;
  date_of_birth: string | null;
}

export interface MembersListResponse {
  members: MemberItem[];
  has_more: boolean;
}

export interface ClaimedRewardItem {
  name: string;
  customer_name: string;
  reward_title: string;
  reward_type: string;
  points_spent: number;
  claimed_at: string | null;
  status: string;
  sales_order: string | null;
  coupon_code: string | null;
  delivery_status: string | null;
}

export interface ClaimedRewardsResponse {
  claims: ClaimedRewardItem[];
  has_more: boolean;
}

export interface ExpiryLogEntry {
  type: "expiry" | "tier_change";
  customer_name: string;
  description: string;
  points: number;
  date: string;
}

export interface ExpiryLogResponse {
  entries: ExpiryLogEntry[];
  has_more: boolean;
}

export interface GeneratedCoupon {
  name: string;
  coupon_code: string;
  pricing_rule: string;
  valid_from: string | null;
  valid_upto: string | null;
  used: number;
  maximum_use: number;
  coupon_type: string;
  expiry_status: "active" | "expiring_soon" | "expired";
}

export interface GeneratedCouponsResponse {
  coupons: GeneratedCoupon[];
  has_more: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export interface TierCouponTemplate {
  loyalty_tier: string;
  customer_group: string;
  coupon_template: string;
  coupons_per_month: number;
  auto_gen_enabled: number;
}

export interface MonthlyCouponConfigResponse {
  templates: TierCouponTemplate[];
  auto_gen_schedule: string;
}

export const rewardsQueryKeys = {
  all: ["rewards"] as const,
  config: ["rewards", "config"] as const,
  earnRules: ["rewards", "earn-rules"] as const,
  catalog: ["rewards", "catalog"] as const,
  metrics: ["rewards", "metrics"] as const,
  monthlyCoupons: ["rewards", "monthly-coupons"] as const,
  members: (params: { search?: string; offset?: number; birthday_filter?: string }) =>
    ["rewards", "members", params] as const,
  claimedRewards: (params: {
    status?: string;
    search?: string;
    offset?: number;
  }) => ["rewards", "claimed-rewards", params] as const,
  expiryLog: (params: { offset?: number }) =>
    ["rewards", "expiry-log", params] as const,
  generatedCoupons: (params: { offset?: number }) =>
    ["rewards", "generated-coupons", params] as const,
  campaigns: (params: { status?: string; offset?: number }) =>
    ["rewards", "campaigns", params] as const,
  campaignDetail: (name: string) =>
    ["rewards", "campaign-detail", name] as const,
  customerHistory: (customer: string, params: { offset?: number }) =>
    ["rewards", "customer-history", customer, params] as const,
  customerClaims: (customer: string) =>
    ["rewards", "customer-claims", customer] as const,
  dropdownOptions: ["rewards", "dropdown-options"] as const,
};

const API = "zaviago_backend.rewards.admin_api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Known TierCardDesign keys — discard anything else (e.g. corrupted character-indexed data). */
const DESIGN_KEYS: (keyof TierCardDesign)[] = [
  "bg_type", "bg_color", "bg_color_end", "bg_angle", "text_color",
  "pattern", "pattern_opacity", "layout", "logo_mode", "logo_image",
  "logo_text", "label_text", "subtitle_text",
];

function sanitizeCardDesign(raw: unknown): TierCardDesign | null {
  if (!raw) return null;
  const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
  if (!obj || typeof obj !== "object" || !("bg_color" in obj)) return null;
  const clean: Record<string, unknown> = {};
  for (const k of DESIGN_KEYS) {
    if (k in obj) clean[k] = obj[k];
  }
  return clean as unknown as TierCardDesign;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * @param storefrontKeyOverride Explicit store to scope to (e.g. a customer's
 *   `custom_primary_storefront`). Falls back to the global active store when
 *   omitted (the /rewards admin page's StoreSelector behaviour).
 */
export function useRewardsConfig(storefrontKeyOverride?: string) {
  const globalSk = useRewardsStoreKey();
  const sk = storefrontKeyOverride ?? globalSk;
  return useQuery<RewardsConfigResponse>({
    queryKey: [...rewardsQueryKeys.config, sk],
    queryFn: () =>
      frappeMethod<RewardsConfigResponse>(`${API}.get_loyalty_program_config`, { storefront_key: sk }),
    placeholderData: keepPreviousData,
    select: (data) => {
      // Frappe JSON fields arrive as strings — parse tier_card_design for each tier.
      // Also sanitize: only keep known design keys (DB may have corrupted character-indexed data).
      if (data.tiers) {
        return {
          ...data,
          tiers: data.tiers.map((t) => ({
            ...t,
            tier_card_design: sanitizeCardDesign(t.tier_card_design),
          })),
        };
      }
      return data;
    },
  });
}

export interface DropdownOptionsResponse {
  customer_groups: string[];
  territories: string[];
  coupon_codes: Array<{ name: string; label: string; type: string }>;
}

export function useDropdownOptions() {
  return useQuery<DropdownOptionsResponse>({
    queryKey: rewardsQueryKeys.dropdownOptions,
    queryFn: () =>
      frappeMethod<DropdownOptionsResponse>(`${API}.get_dropdown_options`),
    placeholderData: keepPreviousData,
  });
}

export function useEarnRules(enabled = true) {
  const sk = useRewardsStoreKey();
  return useQuery<EarnRulesResponse>({
    queryKey: [...rewardsQueryKeys.earnRules, sk],
    queryFn: () =>
      frappeMethod<EarnRulesResponse>(`${API}.get_earn_rules`, { storefront_key: sk }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAdminRewards(enabled = true) {
  const sk = useRewardsStoreKey();
  return useQuery<AdminRewardsResponse>({
    queryKey: [...rewardsQueryKeys.catalog, sk],
    queryFn: () =>
      frappeMethod<AdminRewardsResponse>(`${API}.get_admin_rewards`, { storefront_key: sk }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export type CardLayout = "classic" | "centered" | "minimal" | "badge" | "split" | "bar";
export type LogoMode = "image" | "text" | "none";

export interface TierCardDesign {
  bg_type: "solid" | "gradient";
  bg_color: string;
  bg_color_end?: string;
  bg_angle?: number;
  text_color: string;
  pattern?: string | null;
  pattern_opacity?: number;
  layout?: CardLayout;
  logo_mode?: LogoMode;
  logo_image?: string | null;
  logo_text?: string | null;
  label_text?: string | null;
  subtitle_text?: string | null;
}

export interface TierRow {
  tier_name: string;
  min_spent: number;
  collection_factor: number;
  tier_icon?: string | null;
  tier_card_image?: string | null;
  tier_card_design?: TierCardDesign | null;
}

export function useSetupLoyaltyProgram() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      name: string;
      collection_factor?: number;
      expiry_days: number;
      tiers?: TierRow[];
      conversion_factor?: number;
      auto_opt_in?: number;
      from_date?: string;
      to_date?: string;
      customer_group?: string;
      customer_territory?: string;
    }) =>
      frappeMethod<{ success: boolean; loyalty_program: string }>(
        `${API}.setup_loyalty_program`,
        {
          ...params,
          storefront_key: sk,
          // Serialize tiers as JSON string for Frappe
          tiers: params.tiers ? JSON.stringify(params.tiers) : undefined,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.config });
    },
  });
}

export function useUpdateEarnRule() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      earn_type: string;
      enabled: number;
      points: number;
    }) =>
      frappeMethod<{ success: boolean; name: string }>(
        `${API}.update_earn_rule`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.earnRules });
    },
  });
}

export function useCreateReward() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      title: string;
      reward_type: string;
      points_cost: number;
      description?: string;
      image?: string;
      pricing_rule?: string;
      item_code?: string;
      max_redemptions?: number;
      valid_from?: string;
      valid_upto?: string;
    }) =>
      frappeMethod<{ success: boolean; reward_id: string }>(
        `${API}.create_reward`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.catalog });
    },
  });
}

// ---------------------------------------------------------------------------
// Management hooks (Epic 07)
// ---------------------------------------------------------------------------

export function useRewardsMetrics(enabled = true) {
  const sk = useRewardsStoreKey();
  return useQuery<RewardsMetricsResponse>({
    queryKey: [...rewardsQueryKeys.metrics, sk],
    queryFn: () =>
      frappeMethod<RewardsMetricsResponse>(`${API}.get_rewards_metrics`, { storefront_key: sk }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled,
  });
}

export function useUpdateReward() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      reward_id: string;
      title?: string;
      description?: string;
      points_cost?: number;
      status?: string;
      image?: string;
      max_redemptions?: number;
      valid_from?: string;
      valid_upto?: string;
    }) =>
      frappeMethod<{ success: boolean }>(
        `${API}.update_reward`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.catalog });
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.metrics });
    },
  });
}

export function useMembersList(
  search?: string,
  limit = 20,
  offset = 0,
  enabled = true,
  birthdayFilter?: string,
) {
  const sk = useRewardsStoreKey();
  return useQuery<MembersListResponse>({
    queryKey: [...rewardsQueryKeys.members({ search, offset, birthday_filter: birthdayFilter }), sk],
    queryFn: () =>
      frappeMethod<MembersListResponse>(`${API}.get_members_list`, {
        search: search || undefined,
        limit,
        offset,
        birthday_filter: birthdayFilter || undefined,
        storefront_key: sk,
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useClaimedRewards(
  status?: string,
  search?: string,
  limit = 20,
  offset = 0,
  enabled = true,
) {
  const sk = useRewardsStoreKey();
  return useQuery<ClaimedRewardsResponse>({
    queryKey: [...rewardsQueryKeys.claimedRewards({ status, search, offset }), sk],
    queryFn: () =>
      frappeMethod<ClaimedRewardsResponse>(`${API}.get_claimed_rewards`, {
        status: status || undefined,
        search: search || undefined,
        limit,
        offset,
        storefront_key: sk,
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Monthly Coupon Config (Feature 3 — OPS-1838)
// ---------------------------------------------------------------------------

export function useMonthlyCouponConfig(enabled = true) {
  const sk = useRewardsStoreKey();
  return useQuery<MonthlyCouponConfigResponse>({
    queryKey: [...rewardsQueryKeys.monthlyCoupons, sk],
    queryFn: () =>
      frappeMethod<MonthlyCouponConfigResponse>(
        `${API}.get_monthly_coupon_config`,
        { storefront_key: sk },
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSaveMonthlyCouponConfig() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      templates: TierCouponTemplate[];
      auto_gen_schedule: string;
    }) =>
      frappeMethod<{ success: boolean }>(
        `${API}.save_monthly_coupon_config`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.monthlyCoupons });
    },
  });
}

// ---------------------------------------------------------------------------
// Dashboard — Expiry Log + Generated Coupons (Features 1, 4)
// ---------------------------------------------------------------------------

export function useExpiryActivityLog(limit = 20, offset = 0, enabled = true) {
  return useQuery<ExpiryLogResponse>({
    queryKey: rewardsQueryKeys.expiryLog({ offset }),
    queryFn: () =>
      frappeMethod<ExpiryLogResponse>(`${API}.get_expiry_activity_log`, {
        limit,
        offset,
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useGeneratedCoupons(limit = 20, offset = 0, enabled = true) {
  return useQuery<GeneratedCouponsResponse>({
    queryKey: rewardsQueryKeys.generatedCoupons({ offset }),
    queryFn: () =>
      frappeMethod<GeneratedCouponsResponse>(`${API}.get_generated_coupons`, {
        limit,
        offset,
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Campaign System (Feature 9 — OPS-1862)
// ---------------------------------------------------------------------------

const CAMPAIGN_API = "zaviago_backend.rewards.admin_campaigns";

export interface CampaignItem {
  name: string;
  campaign_name: string;
  audience_type: string;
  reward_type: string;
  reward_points: number;
  reward_coupon_template: string;
  reward_multiplier: number;
  start_date: string | null;
  end_date: string | null;
  status: string;
  members_targeted: number;
  executed_at: string | null;
}

export interface CampaignListResponse {
  campaigns: CampaignItem[];
  has_more: boolean;
}

export interface CampaignDetail extends CampaignItem {
  description: string;
  company: string;
  audience_tier: string;
  audience_min_orders: number;
  audience_min_spent: number;
}

// Feature 10 — Point History
export interface PointHistoryEntry {
  name: string;
  points: number;
  purchase_amount: number;
  posting_date: string | null;
  expiry_date: string | null;
  invoice: string;
  earn_source: string;
  description: string;
}

export interface PointHistoryResponse {
  entries: PointHistoryEntry[];
  current_balance: number;
  has_more: boolean;
}

// Feature 10 — Bonus Points
export interface BonusPointsResponse {
  success: boolean;
  lpe_name: string;
  points: number;
  earn_source: string;
}

export function useCampaignList(
  statusFilter?: string,
  limit = 20,
  offset = 0,
  enabled = true,
) {
  const sk = useRewardsStoreKey();
  return useQuery<CampaignListResponse>({
    queryKey: [...rewardsQueryKeys.campaigns({ status: statusFilter, offset }), sk],
    queryFn: () =>
      frappeMethod<CampaignListResponse>(`${CAMPAIGN_API}.get_campaigns`, {
        status_filter: statusFilter || undefined,
        limit,
        offset,
        storefront_key: sk,
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCampaignDetail(name: string, enabled = true) {
  const sk = useRewardsStoreKey();
  return useQuery<CampaignDetail>({
    queryKey: [...rewardsQueryKeys.campaignDetail(name), sk],
    queryFn: () =>
      frappeMethod<CampaignDetail>(`${CAMPAIGN_API}.get_campaign_detail`, {
        name,
        storefront_key: sk,
      }),
    enabled: !!name && enabled,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      campaign_name: string;
      description?: string;
      audience_type: string;
      audience_tier?: string;
      audience_min_orders?: number;
      audience_min_spent?: number;
      reward_type: string;
      reward_points?: number;
      reward_coupon_template?: string;
      reward_multiplier?: number;
      start_date?: string;
      end_date?: string;
    }) =>
      frappeMethod<{ name: string; status: string }>(
        `${CAMPAIGN_API}.create_campaign`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.all });
    },
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      name: string;
      campaign_name?: string;
      description?: string;
      audience_type?: string;
      audience_tier?: string;
      audience_min_orders?: number;
      audience_min_spent?: number;
      reward_type?: string;
      reward_points?: number;
      reward_coupon_template?: string;
      reward_multiplier?: number;
      start_date?: string;
      end_date?: string;
    }) =>
      frappeMethod<{ name: string; status: string }>(
        `${CAMPAIGN_API}.update_campaign`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.all });
    },
  });
}

export function useActivateCampaign() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (name: string) =>
      frappeMethod<{ name: string; status: string; members_targeted: number }>(
        `${CAMPAIGN_API}.activate_campaign`,
        { name, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.all });
    },
  });
}

export function useEndCampaign() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (name: string) =>
      frappeMethod<{ name: string; status: string }>(
        `${CAMPAIGN_API}.end_campaign`,
        { name, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.all });
    },
  });
}


// ---------------------------------------------------------------------------
// Feature 10 — Customer Point History + Manual Bonus
// ---------------------------------------------------------------------------

export function useCustomerPointHistory(
  customer: string,
  limit = 50,
  offset = 0,
  enabled = true,
) {
  return useQuery<PointHistoryResponse>({
    queryKey: rewardsQueryKeys.customerHistory(customer, { offset }),
    queryFn: () =>
      frappeMethod<PointHistoryResponse>(`${API}.get_customer_point_history`, {
        customer,
        limit,
        offset,
      }),
    placeholderData: keepPreviousData,
    enabled: !!customer && enabled,
  });
}

export function useCustomerClaimedRewards(customer: string, enabled = true) {
  return useQuery<ClaimedRewardsResponse>({
    queryKey: rewardsQueryKeys.customerClaims(customer),
    queryFn: () =>
      frappeMethod<ClaimedRewardsResponse>(
        `${API}.get_customer_claimed_rewards`,
        { customer },
      ),
    placeholderData: keepPreviousData,
    enabled: !!customer && enabled,
  });
}

export function useAwardBonusPoints() {
  const qc = useQueryClient();
  const sk = useRewardsStoreKey();
  return useMutation({
    mutationFn: (params: {
      customer: string;
      points: number;
      reason: string;
    }) =>
      frappeMethod<BonusPointsResponse>(
        `${API}.award_bonus_points`,
        { ...params, storefront_key: sk },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rewardsQueryKeys.all });
    },
    onError: (_err: Error) => {
      // Handled by caller
    },
  });
}
